import * as React from "react";
import {
    Bar,
    BarChart,
    BarShapeProps,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    TooltipContentProps,
    XAxis,
    YAxis
} from "recharts";
import {addMissingDates, getBarColor} from "../inform/informUtil";
import {AonColors} from "../../../../css/Colors";

export interface IHorizontalBarChart {
    label: string;
    value: number;
    isMissing?: boolean;
}

export interface IHorizontalBarChartProps {
    data: IHorizontalBarChart[];
    tooltipTitle?: string;
    tooltipLabel?: string;
    chartHeight?: number;
    valueFormatFunction?: (value: number | string) => string;
    xAxisFormater?: (value: string) => string;
    yAxisFormater?: (value: number) => string;
}

export interface ICustomTooltipProps {
    title: string;
    label: string;
    value: string | number | undefined;
}

export const CustomChartTooltip: React.FunctionComponent<ICustomTooltipProps> = (props) => {
    return (
        <div className="chart__tooltip">
            <div className="chart__tooltip-title">{props.title}</div>
            <div className="chart__tooltip-value-label">{props.label}</div>
            <div className="chart__tooltip-value">{props.value}</div>
        </div>
    );
};

export const HorizontalBarChart: React.FunctionComponent<IHorizontalBarChartProps> = (props) => {
    const chartData = addMissingDates(props.data);
    const getCustomTooltip = (tooltipProps: TooltipContentProps): React.ReactNode => {
        const { active, payload, label } = tooltipProps;

        if (active && payload && payload.length) {
            const tooltipValue = payload[0].payload.isMissing ? "N/A" : (payload[0].value as number);
            const customTooltipProps: ICustomTooltipProps = {
                title: props.tooltipTitle ? props.tooltipTitle : String(label ?? ""),
                label: props.tooltipLabel ? props.tooltipLabel : String(label ?? ""),
                value: props.valueFormatFunction ? props.valueFormatFunction(tooltipValue!) : tooltipValue
            };

            return <CustomChartTooltip {...customTooltipProps} />;
        }

        return null;
    };

    return <div className="horizontal-bar-chart__container" data-testid="horizontal-bar-chart__container">
        <ResponsiveContainer width="100%" height={props.chartHeight ? props.chartHeight : 400}>
            <BarChart
                height={props.chartHeight ? props.chartHeight : 400}
                data={chartData}
                margin={{top: 15, right: 0, left: 20, bottom: 0,}}
                {...{overflow: "visible"}}
            >
                <CartesianGrid stroke="#ccc" vertical={false}/>
                <XAxis axisLine={false} tickLine={false} dataKey="label"
                       interval={0}
                       tick={{fontSize: 12}}
                       tickFormatter={props.xAxisFormater ? props.xAxisFormater : undefined}
                       className={"axis-label"}
                />
                <YAxis axisLine={false} tickLine={false} padding={{top: 0, bottom: 0}}
                       tick={{fontSize: 12}}
                       tickFormatter={props.yAxisFormater ? props.yAxisFormater : undefined}
                       domain={["auto", "auto"]}
                       className={"axis-label"}
                       dx={-15}
                />
                <Tooltip content={getCustomTooltip}/>
                <ReferenceLine y={0} stroke={AonColors.AonBlack}/>
                <Bar dataKey="value" maxBarSize={15}
                    shape={({x = 0, y = 0, width = 0, height = 0, payload}: BarShapeProps) => (
                        <rect x={x} y={height < 0 ? y + height : y} width={width} height={Math.abs(height)}
                            fill={getBarColor(payload.value)} cursor="pointer"/>
                    )}
                />
            </BarChart>
        </ResponsiveContainer>
    </div>;
};